import {
  Component, Input, Output, EventEmitter,
  ChangeDetectionStrategy, OnChanges, SimpleChanges,
  OnInit, ChangeDetectorRef, ViewChild, OnDestroy, DoCheck
} from '@angular/core';
import * as sharedConstants from '../shared.constants';
import { FormBuilder, FormGroup, FormControl } from '@angular/forms';
import * as _ from 'lodash';
import { TreeModel } from './models/tree.model';
import { AppVirtualTreeComponent } from './virtual-tree/virtual-tree.component';
import { Subscription, BehaviorSubject, Observable } from 'rxjs';


@Component({
  selector: 'app-treeview',
  templateUrl: './tree-view.component.html',
  styleUrls: ['./tree-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TreeViewComponent implements OnInit, OnChanges, OnDestroy {
  collapse = true;
  public readonly treeOrderType = sharedConstants.treeOrderType;
  public readonly treeOrderDirection = sharedConstants.treeOrderDirection;
  lastExpandedItemTreeId: number;

  private tree$ = new BehaviorSubject<any>(null);
  @Input() set tree(value: any) {
    this.tree$.next(value);
    this.filterForm.reset({
      textInput: '',
      text: '',
      attributeDataType: [],
      group: [],
      domains: [],
      showLinkeds: false,
      reverseOrder: false,
      customOrder: this.customSortField
    });

  }
  get tree(): any {
    return this.tree$.getValue();
  }

  // @Input() tree: any;
  @Output() selectedElementChanged = new EventEmitter<any[]>();
  @Output() deleteElement = new EventEmitter<any>();
  @Output() restoreElement = new EventEmitter<any>();
  @Output() sameElementSelected = new EventEmitter<any>();
  @Output() addSelectedElement = new EventEmitter<any>();
  // tslint:disable-next-line: no-output-on-prefix
  @Output() onFilterChange = new EventEmitter<any>();
  @Output() afterLoadingComplete = new EventEmitter<any>();
  @Output() confirmSelection = new EventEmitter<any>();

  @Input() showFilters = true;
  @Input() showAdvancedFilters = true;
  @Input() showLegend = false;
  @Input() showAddButton = false;
  @Input() selectFirstFound = true;
  @Input() selectedItem = null;
  @Input() deletePermission = sharedConstants.TREE_DEFAULT_DELETE_PERMISSION;
  @Input() maxDepth = 0;
  @Input() idField = '';
  @Input() customSortField = '';
  @Input() showExpanded = false;
  @Input() selectionCompleted: Observable<any>;
  @Input() selectable = true;
  @Input() showDeactivatedAttributes = false;
  @Input() showDeactivatedValues = false;

  @Input() set filterData(data: any) {
    this.filterForm.patchValue(data);
  }

  @ViewChild('vTree', { static: false }) vTree: AppVirtualTreeComponent;

  filterForm: FormGroup;

  public treeModel = new TreeModel<any>({
    canExpand: (item) => item && item.children && item.children.length > 0,
    childAccessor: (item) => item.children,
    idAccessorField: this.idField,
  });
  public treeView: any;

  private searchTextTimeout;

  private firstMatchFound = null;
  public EXPOSE_CONSTANTS = sharedConstants;

  private subscriptions: {
    [key: string]: Subscription
  } = {};

  public checkedByUser = null;
  public showSpinner = false;

  constructor(
    private readonly fb: FormBuilder,
    public readonly cd: ChangeDetectorRef
  ) {
    this.filterForm = this.fb.group({
      textInput: new FormControl(''),
      text: new FormControl(''),
      reverseOrder: new FormControl(false),
      customOrder: new FormControl(this.customSortField),
    });
  }

  ngOnInit(): void {
    this.subscriptions.selectionChange = this.treeModel.onSelectionChanged.subscribe(
      (selected) => this.onSelectionChange(selected, this.treeModel.query.findNode(selected))
    );
    this.subscriptions.sameElementSelected = this.treeModel.onSameElementSelected.subscribe(
      (selected) => this.onSameElementSelected(selected, this.treeModel.query.findNode(selected))
    );

    if (this.showExpanded) {
      this.treeModel.expandAll();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.tree) {
      this.treeModel.updateConfig({
        canExpand: (item) => item && item.children && item.children.length > 0,
        childAccessor: (item) => item.children,
        idAccessorField: this.idField,
      });
      this.filterForm.patchValue({ customOrder: this.customSortField });
      const previouslySelectedItem = this.treeModel.getSelectedItem();
      this.generateTreeView(changes.tree.currentValue, false, previouslySelectedItem);

      this.showSpinner = false;
      this.afterLoadingComplete.emit();
    }

    if (changes.selectedNodes) {
      this.checkedByUser = changes.selectedNodes.currentValue;
    }
  }

  ngOnDestroy(): void {
    Object.values(this.subscriptions).forEach(subscription => subscription.unsubscribe());
  }

  invalidateSize(): void {
    this.vTree.invalidateSize();
  }

  markForCheck(): void {
    this.cd.markForCheck();
  }

  onExpandAll(): void {
    this.treeModel.expandAll();
  }

  onCollapseAll(): void {
    this.treeModel.collapseAll();
  }

  onAdvancedFiltersClick(): void {
    this.collapse = !this.collapse;

    setTimeout(() => {
      this.treeModel.reloadTree();

    }, 500);
  }

  selectItem(item: any): void {
    if (this.selectable) {
      const node = this.treeModel.query.findNode(item);

      if (node && node.item) {
        this.treeModel.selectAndHighlight(node.item);
      }

      if (!item || !node) {
        this.selectedElementChanged.emit([null, null, null]);
        return;
      }
    }
  }

  private onSelectionChange(event, node): void {
    if (node && node.parent) {
      const indexString = this.generateIndexString(node);
      this.selectedElementChanged.emit([
        [indexString],
        this.treeView,
        {
          ...event,
          parent: node.parent.item
        }
      ]);
    }
  }

  unselect(): void {
    this.treeModel.selectAndHighlight(null);
  }

  selectFirst(): void {
    this.treeModel.selectFirst();
  }

  onSameElementSelected(event, node): void {
    if (!event || !node) {
      return;
    }

    const indexString = this.generateIndexString(node);
    this.sameElementSelected.emit([
      [indexString],
      this.treeView,
      {
        ...event,
        parent: node.parent.item
      }
    ]);
  }

  onSearchTextChange(text: string): void {
    this.filterForm.get('text').setValue(text);
    clearTimeout(this.searchTextTimeout);
    this.performSearch();
  }

  performSearch(): void {
    this.searchTextTimeout = setTimeout(
      () => {
        this.generateTreeView(this.tree, true);
        this.cd.markForCheck();
        this.expandFound();
      },
      250
    );
  }

  onClearAllClick(): void {
    this.filterForm.reset({
      textInput: '',
      text: '',
      reverseOrder: false,
      customOrder: this.customSortField
    });
    this.generateTreeView(this.tree);
    this.onCollapseAll();
  }

  onOrderToggleClick(treeOrderDir: sharedConstants.treeOrderDirection, treeOrderType: sharedConstants.treeOrderType): void {
    const dirformField = this.filterForm.get('reverseOrder');
    const customformField = this.filterForm.get('customOrder');

    dirformField.setValue(treeOrderDir === sharedConstants.treeOrderDirection.asc ? false : true);
    customformField.setValue(treeOrderType === sharedConstants.treeOrderType.custom ? this.customSortField : '');
    this.generateTreeView(this.tree);
  }

  recursiveSort(items, reverse = false, customOrder = ''): void {
    items.sort((obj1, obj2) => {
      return this.sortTwoNodes(obj1, obj2, customOrder);
    });

    items.forEach((item) => {
      if (item.children) {
        item.children = this.recursiveSort(item.children, reverse, customOrder);
      }
    });

    return reverse ? this.reverseTreeNodeList(items) : items;
  }

  sortTwoNodes(obj1: any, obj2: any, customOrder = ''): any {
    if (customOrder && typeof obj2[customOrder] === 'number' && obj2[customOrder]) {
      return obj1[customOrder] - obj2[customOrder];
    } else {
      return ('' + obj1.description).localeCompare(obj2.description);
    }
  }

  reverseTreeNodeList(treeClone: any): any {
    const savedNodeArray = [];
    let bar = treeClone;
    bar.reverse();

    if (savedNodeArray.length > 0) {
      bar = savedNodeArray.concat(bar);
    }

    return bar;
  }

  private generateTreeView(tree: any, reset = false, previouslySelectedItem = null): void {
    if (!tree || (tree[0] && tree[0].length === 0)) {
      return;
    }
    this.treeView = [];
    this.firstMatchFound = null;
    this.cd.markForCheck();
    tree = _.cloneDeep(tree);
    this.treeView = this.filterTree(this.recursiveSort(tree, this.filterForm.get('reverseOrder').value
      , this.filterForm.get('customOrder').value));
    this.getFirstMatch(this.treeView);

    this.onFilterChange.emit({
      isTreeBlank: this.treeView.length === 0,
      areFiltersEmpty: this.areFiltersEmpty()
    });

    this.treeModel.collapseAll();
    this.treeModel.load(this.treeView);

    // RELOAD BECAUSE CONTAINER SIZE MAY CHANGE
    setTimeout(
      () => {
        this.treeModel.reloadTree();

        if (this.selectedItem) {
          const nodeToSelect = this.treeModel.getItemBy('id', this.selectedItem.id);
          if (nodeToSelect) {
            this.selectItem(nodeToSelect[0]);
            this.treeModel.expandToSelectedItem();
          }
          return;
        }

        if (this.selectFirstFound) {
          if (previouslySelectedItem) {
            const nodeToSelect = this.treeModel.getItemBy('treeId', previouslySelectedItem.treeId);
            if (nodeToSelect && nodeToSelect.length > 0) {
              this.selectItem(nodeToSelect[0]);
            } else if (this.firstMatchFound) {
              this.selectItem(this.firstMatchFound);
            }
          } else if (this.firstMatchFound) {
            this.selectItem(this.firstMatchFound);
            this.treeModel.expandToSelectedItem();
          }
        }
      },
      500
    );
  }

  private filterTree(tree: any, parent = { id: 0 }): any {
    const textSearch = this.filterForm.get('text') ? this.filterForm.get('text').value : '';
    const textSearchRegexp = new RegExp(textSearch, 'gi');

    return tree.filter(
      (item) => {

        if (!item) {
          return false;
        }

        item.treeId = item.description + item.id + parent.id;
        item.label = item.description;
        let copyChildren = _.cloneDeep(item.children);
        if (item.children) {
          item.children.forEach(child => {
            child.treeId = child.description + child.id + item.id;
            child.label = child.description;
          });
        }
        item.children = item.children ? this.filterTree(item.children, item) : [];

        const taxCode = item.identifier;
        const hasChildren = item.children && item.children.length > 0;

        const foundByTextSearch = item.label.search(textSearchRegexp) !== -1 || (taxCode && taxCode.toString() === textSearch);

        item.foundByTextSearch = textSearch.length > 0 && foundByTextSearch;

        if (textSearch.length > 0 && foundByTextSearch) {
          item.label = item.label.replace(textSearchRegexp, match => `<span class="text--highlight">${match}</span>`);
        }

        if (textSearch.length > 0 && !foundByTextSearch) {
          return hasChildren;
        }

        if (foundByTextSearch && copyChildren && copyChildren.length > 0) {
          if (!this.showDeactivatedValues) {
            copyChildren = copyChildren.filter(copyItem => copyItem.active);
          }
          item.children = copyChildren;
        }

        return foundByTextSearch || hasChildren;
      },
      []
    );
  }

  private getFirstMatch(tree: any): void {

    tree.forEach((item) => {
      if (this.firstMatchFound) {
        return;
      }
      if (item.foundByTextSearch || item.foundByFilters) {
        this.firstMatchFound = item;
        return;
      }
      if (item.children) {
        this.getFirstMatch(item.children);
      }
    });
  }

  private expandFound(): void {
    if (!this.areFiltersEmpty()) {
      this.treeModel.expandAll();
    }
  }

  private generateIndexString(node, result = ''): string {

    if (node.parent && node.parent.depth !== -1) {
      result = result + this.generateIndexString(node.parent, result);
    }

    result = result.length > 0 ?
      `${result}_${node.index}` : `${node.index}`;

    return result;
  }

  private areFiltersEmpty(): boolean {
    return !(this.filterForm.get('text') && this.filterForm.get('text').value !== '' ||
      this.filterForm.get('text').value.length > 0
    );
  }

  getNodeClasses(node: any): string[] {
    const classes = [];
    if (this.treeModel.isHighlighted(node.item)) {
      classes.push('k-state-focused');
    }
    if (this.treeModel.isSelected(node.item)) {
      classes.push('k-state-selected');
    }

    return classes;
  }

  expandItem(item: any): void {
    return this.treeModel.toggle(item);
  }

  getItemLabel(item): string {
    let label = item.description;
    const textSearch = this.filterForm.get('text') ? this.filterForm.get('text').value : '';
    const textSearchRegexp = new RegExp(textSearch, 'gi');
    const foundByTextSearch = label.search(textSearchRegexp) !== -1;

    if (textSearch.length > 0 && foundByTextSearch) {
      label = label.replace(textSearchRegexp, match => `<span class="text--highlight">${match}</span>`);
    }

    return label;
  }

  onConfirmSelection(): void {
    this.confirmSelection.emit();
  }
}
